import type { LocaleCode } from "./locale-config";

export type LocalizedSlugMap = Record<LocaleCode, string>;

export type DirectorySegmentKey =
  | "services"
  | "serviceAreas"
  | "emergencyRoofing"
  | "commercialRoofing"
  | "strataRoofing"
  | "residentialRoofing"
  | "roofSystems"
  | "roofEstimate"
  | "beforeAfter"
  | "campaigns"
  | "blog"
  | "about"
  | "faq"
  | "contact"
  | "privacy"
  | "terms"
  | "thankYou";

function slugAll(enSlug: string, localized: Omit<LocalizedSlugMap, "en-ca">): LocalizedSlugMap {
  return { "en-ca": enSlug, ...localized };
}

export const DIRECTORY_SEGMENTS: Record<DirectorySegmentKey, LocalizedSlugMap> = {
  services: {
    "en-ca": "services",
    "zh-cn": "服务",
    "zh-tw": "服務",
    "pa-in": "ਸੇਵਾਵਾਂ",
    "ko-kr": "서비스",
    "fa-ir": "خدمات",
    "ar-sa": "الخدمات",
  },
  serviceAreas: {
    "en-ca": "service-areas",
    "zh-cn": "服务区域",
    "zh-tw": "服務區域",
    "pa-in": "ਸੇਵਾ-ਖੇਤਰ",
    "ko-kr": "서비스-지역",
    "fa-ir": "مناطق-خدماتی",
    "ar-sa": "مناطق-الخدمة",
  },
  emergencyRoofing: {
    "en-ca": "emergency-roofing",
    "zh-cn": "紧急屋顶服务",
    "zh-tw": "緊急屋頂服務",
    "pa-in": "ਐਮਰਜੈਂਸੀ-ਛੱਤ-ਸੇਵਾਵਾਂ",
    "ko-kr": "긴급-지붕-서비스",
    "fa-ir": "خدمات-اضطراری-سقف",
    "ar-sa": "خدمات-الأسقف-الطارئة",
  },
  commercialRoofing: {
    "en-ca": "commercial-roofing",
    "zh-cn": "商业屋顶",
    "zh-tw": "商業屋頂",
    "pa-in": "ਵਪਾਰਕ-ਛੱਤਾਂ",
    "ko-kr": "상업용-지붕",
    "fa-ir": "سقف-تجاری",
    "ar-sa": "أسقف-تجارية",
  },
  strataRoofing: {
    "en-ca": "strata-roofing",
    "zh-cn": "共管物业屋顶",
    "zh-tw": "共管物業屋頂",
    "pa-in": "ਸਾਂਝੀ-ਮਲਕੀਅਤ-ਛੱਤਾਂ",
    "ko-kr": "공동주택-지붕",
    "fa-ir": "سقف-مجتمع‌های-مسکونی",
    "ar-sa": "أسقف-المجمعات-السكنية",
  },
  residentialRoofing: {
    "en-ca": "residential-roofing",
    "zh-cn": "住宅屋顶",
    "zh-tw": "住宅屋頂",
    "pa-in": "ਰਿਹਾਇਸ਼ੀ-ਛੱਤਾਂ",
    "ko-kr": "주택-지붕",
    "fa-ir": "سقف-مسکونی",
    "ar-sa": "أسقف-سكنية",
  },
  roofSystems: {
    "en-ca": "roof-systems",
    "zh-cn": "屋顶系统",
    "zh-tw": "屋頂系統",
    "pa-in": "ਛੱਤ-ਪ੍ਰਣਾਲੀਆਂ",
    "ko-kr": "지붕-시스템",
    "fa-ir": "سیستم‌های-سقف",
    "ar-sa": "أنظمة-الأسقف",
  },
  roofEstimate: {
    "en-ca": "roof-estimate",
    "zh-cn": "屋顶估价",
    "zh-tw": "屋頂估價",
    "pa-in": "ਛੱਤ-ਦਾ-ਅਨੁਮਾਨ",
    "ko-kr": "지붕-견적",
    "fa-ir": "برآورد-هزینه-سقف",
    "ar-sa": "تقدير-تكلفة-السقف",
  },
  beforeAfter: {
    "en-ca": "before-after",
    "zh-cn": "施工前后",
    "zh-tw": "施工前後",
    "pa-in": "ਪਹਿਲਾਂ-ਬਾਅਦ",
    "ko-kr": "시공-전후",
    "fa-ir": "قبل-و-بعد",
    "ar-sa": "قبل-وبعد",
  },
  campaigns: {
    "en-ca": "campaigns",
    "zh-cn": "推广",
    "zh-tw": "推廣",
    "pa-in": "ਮੁਹਿੰਮਾਂ",
    "ko-kr": "캠페인",
    "fa-ir": "کمپین",
    "ar-sa": "حملات",
  },
  blog: {
    "en-ca": "blog",
    "zh-cn": "blog",
    "zh-tw": "blog",
    "pa-in": "blog",
    "ko-kr": "blog",
    "fa-ir": "blog",
    "ar-sa": "blog",
  },
  about: {
    "en-ca": "about",
    "zh-cn": "关于我们",
    "zh-tw": "關於我們",
    "pa-in": "ਸਾਡੇ-ਬਾਰੇ",
    "ko-kr": "회사-소개",
    "fa-ir": "درباره-ما",
    "ar-sa": "من-نحن",
  },
  faq: {
    "en-ca": "faq",
    "zh-cn": "常见问题",
    "zh-tw": "常見問題",
    "pa-in": "ਅਕਸਰ-ਪੁੱਛੇ-ਸਵਾਲ",
    "ko-kr": "자주-묻는-질문",
    "fa-ir": "پرسش‌های-متداول",
    "ar-sa": "الأسئلة-الشائعة",
  },
  contact: {
    "en-ca": "contact",
    "zh-cn": "联系我们",
    "zh-tw": "聯絡我們",
    "pa-in": "ਸੰਪਰਕ",
    "ko-kr": "문의",
    "fa-ir": "تماس",
    "ar-sa": "تواصل-معنا",
  },
  privacy: {
    "en-ca": "privacy",
    "zh-cn": "隐私政策",
    "zh-tw": "隱私政策",
    "pa-in": "ਗੋਪਨੀਯਤਾ",
    "ko-kr": "개인정보",
    "fa-ir": "حریم-خصوصی",
    "ar-sa": "الخصوصية",
  },
  terms: {
    "en-ca": "terms",
    "zh-cn": "使用条款",
    "zh-tw": "使用條款",
    "pa-in": "ਸ਼ਰਤਾਂ",
    "ko-kr": "이용약관",
    "fa-ir": "شرایط",
    "ar-sa": "الشروط",
  },
  thankYou: {
    "en-ca": "thank-you",
    "zh-cn": "感谢",
    "zh-tw": "感謝",
    "pa-in": "ਧੰਨਵਾਦ",
    "ko-kr": "감사합니다",
    "fa-ir": "سپاس",
    "ar-sa": "شكرا",
  },
};

/** Localized entity slug for the province-wide scheduled-project hub. */
export const BRITISH_COLUMBIA_HUB_SLUGS: LocalizedSlugMap = slugAll("british-columbia", {
  "zh-cn": "不列颠哥伦比亚",
  "zh-tw": "不列顛哥倫比亞",
  "pa-in": "ਬ੍ਰਿਟਿਸ਼-ਕੋਲੰਬੀਆ",
  "ko-kr": "브리티시컬럼비아",
  "fa-ir": "بریتیش-کلمبیا",
  "ar-sa": "بريتيش-كولومبيا",
});

export const SERVICE_ENTITY_SLUGS: Record<string, LocalizedSlugMap> = {
  "emergency-roof-leak-repair": slugAll("emergency-roof-leak-repair", {
    "zh-cn": "紧急屋顶漏水维修",
    "zh-tw": "緊急屋頂漏水維修",
    "pa-in": "ਐਮਰਜੈਂਸੀ-ਛੱਤ-ਲੀਕ-ਮੁਰੰਮਤ",
    "ko-kr": "긴급-지붕-누수-수리",
    "fa-ir": "تعمیر-اضطراری-نشت-سقف",
    "ar-sa": "إصلاح-تسرب-الطوارئ",
  }),
  // NEEDS HUMAN REVIEW (native speaker): "flashing" is transliterated in pa/ko/fa/ar
  // (ਫਲੈਸ਼ਿੰਗ / 플래싱 / فلاشینگ / فلاشينغ) — confirm this matches how each audience searches.
  "flashing-roof-penetration-repair": slugAll("flashing-roof-penetration-repair", {
    "zh-cn": "泛水与穿透点维修",
    "zh-tw": "泛水與穿透點維修",
    "pa-in": "ਫਲੈਸ਼ਿੰਗ-ਪ੍ਰਵੇਸ਼-ਮੁਰੰਮਤ",
    "ko-kr": "플래싱-관통부-수리",
    "fa-ir": "تعمیر-فلاشینگ-و-نفوذ",
    "ar-sa": "إصلاح-الفلاشينغ-والاختراق",
  }),
  // ko: 빗물받이 = "rain gutter" (배수구 reads as floor/sewer drain).
  // fa: ناودان is the standard roof-gutter term (آبرو primarily reads as "honour").
  // NEEDS HUMAN REVIEW (native speaker): ko-kr and fa-ir gutter terminology across all gutter services.
  "moss-removal-roof-gutter": slugAll("moss-removal-roof-gutter", {
    "zh-cn": "苔藓清除与檐沟清理",
    "zh-tw": "苔蘚清除與簷溝清理",
    "pa-in": "ਕਾਈ-ਹਟਾਉਣਾ-ਗਟਰ",
    "ko-kr": "이끼-제거-빗물받이",
    "fa-ir": "حذف-خزه-و-ناودان",
    "ar-sa": "إزالة-الطحالب-والمزاريب",
  }),
  "asphalt-shingle-replacement": slugAll("asphalt-shingle-replacement", {
    "zh-cn": "沥青瓦屋顶更换",
    "zh-tw": "瀝青瓦屋頂更換",
    "pa-in": "ਅਸਫਾਲਟ-ਸ਼ਿੰਗਲ-ਬਦਲੀ",
    "ko-kr": "아스팔트-싱글-교체",
    "fa-ir": "تعویض-شینگل-آسفالتی",
    "ar-sa": "استبدال-الألواح-الإسفلتية",
  }),
  "roof-health-check-maintenance": slugAll("roof-health-check-maintenance", {
    "zh-cn": "屋顶健康检查与维护",
    "zh-tw": "屋頂健康檢查與維護",
    "pa-in": "ਛੱਤ-ਜਾਂਚ-ਰੱਖ-ਰਖਾਵ",
    "ko-kr": "지붕-점검-유지관리",
    "fa-ir": "بازرسی-و-نگهداری-سقف",
    "ar-sa": "فحص-وصيانة-السقف",
  }),
  "shingle-spot-repair": slugAll("shingle-spot-repair", {
    "zh-cn": "瓦片局部维修",
    "zh-tw": "瓦片局部維修",
    "pa-in": "ਸ਼ਿੰਗਲ-ਸਥਾਨਕ-ਮੁਰੰਮਤ",
    "ko-kr": "싱글-부분-수리",
    "fa-ir": "تعمیر-موضعی-شینگل",
    "ar-sa": "إصلاح-موضعي-للألواح",
  }),
  "gutter-cleaning-downspout-flush": slugAll("gutter-cleaning-downspout-flush", {
    "zh-cn": "檐沟清洁与落水管冲洗",
    "zh-tw": "簷溝清潔與落水管沖洗",
    "pa-in": "ਗਟਰ-ਸਫਾਈ",
    "ko-kr": "빗물받이-청소",
    "fa-ir": "نظافت-ناودان",
    "ar-sa": "تنظيف-المزاريب",
  }),
  "storm-tarping-emergency-dry-in": slugAll("storm-tarping-emergency-dry-in", {
    "zh-cn": "风暴临时遮盖与防雨",
    "zh-tw": "風暴臨時遮蓋與防雨",
    "pa-in": "ਤੂਫਾਨ-ਤਰਪਾਲ",
    "ko-kr": "폭풍-방수-덮개",
    "fa-ir": "پوشش-اضطراری-طوفان",
    "ar-sa": "تغطية-طوارئ-العواصف",
  }),
  "skylight-leak-flashing-repair": slugAll("skylight-leak-flashing-repair", {
    "zh-cn": "天窗漏水与泛水维修",
    "zh-tw": "天窗漏水與泛水維修",
    "pa-in": "ਸਕਾਈਲਾਈਟ-ਲੀਕ-ਮੁਰੰਮਤ",
    "ko-kr": "천창-누수-수리",
    "fa-ir": "تعمیر-نشت-نورگیر",
    "ar-sa": "إصلاح-تسرب-النوافذ-السقفية",
  }),
  "chimney-flashing-repair": slugAll("chimney-flashing-repair", {
    "zh-cn": "烟囱泛水维修",
    "zh-tw": "煙囪泛水維修",
    "pa-in": "ਚਿਮਨੀ-ਫਲੈਸ਼ਿੰਗ-ਮੁਰੰਮਤ",
    "ko-kr": "굴뚝-플래싱-수리",
    "fa-ir": "تعمیر-فلاشینگ-دودکش",
    "ar-sa": "إصلاح-فلاشينغ-المدخنة",
  }),
  "gutter-replacement": slugAll("gutter-replacement", {
    "zh-cn": "檐沟更换",
    "zh-tw": "簷溝更換",
    "pa-in": "ਗਟਰ-ਬਦਲੀ",
    "ko-kr": "빗물받이-교체",
    "fa-ir": "تعویض-ناودان",
    "ar-sa": "استبدال-المزاريب",
  }),
  "gutter-guard-installation": slugAll("gutter-guard-installation", {
    "zh-cn": "檐沟防护网安装",
    "zh-tw": "簷溝防護網安裝",
    "pa-in": "ਗਟਰ-ਗਾਰਡ-ਇੰਸਟਾਲ",
    "ko-kr": "빗물받이-가드-설치",
    "fa-ir": "نصب-محافظ-ناودان",
    "ar-sa": "تركيب-حماية-المزاريب",
  }),
  "roof-ventilation-upgrade": slugAll("roof-ventilation-upgrade", {
    "zh-cn": "屋顶通风升级",
    "zh-tw": "屋頂通風升級",
    "pa-in": "ਛੱਤ-ਹਵਾਦਾਰੀ-ਅਪਗ੍ਰੇਡ",
    "ko-kr": "지붕-환기-개선",
    "fa-ir": "ارتقای-تهویه-سقف",
    "ar-sa": "ترقية-تهوية-السقف",
  }),
  "fascia-soffit-repair-replacement": slugAll("fascia-soffit-repair-replacement", {
    "zh-cn": "封檐板与檐底维修更换",
    "zh-tw": "封簷板與簷底維修更換",
    "pa-in": "ਫੈਸੀਆ-ਸੌਫਿਟ-ਮੁਰੰਮਤ",
    "ko-kr": "페시아-소피트-수리",
    "fa-ir": "تعمیر-فاسیا-و-سافیت",
    "ar-sa": "إصلاح-الواجهات-والسقف-المخفي",
  }),
  // NEEDS HUMAN REVIEW (native speaker): fa-ir uses "شینگل سدر" (cedar shingle) for cedar
  // shakes; confirm whether "سقف چوبی سدر" (cedar wood roof) matches search intent better.
  "cedar-shake-repair-restoration": slugAll("cedar-shake-repair-restoration", {
    "zh-cn": "雪松木瓦维修与修复",
    "zh-tw": "雪松木瓦維修與修復",
    "pa-in": "ਸੀਡਰ-ਸ਼ੇਕ-ਮੁਰੰਮਤ",
    "ko-kr": "시더-쉐이크-수리",
    "fa-ir": "تعمیر-شینگل-سدر",
    "ar-sa": "إصلاح-ألواح-الأرز",
  }),
  "flat-low-slope-roof-repair": slugAll("flat-low-slope-roof-repair", {
    "zh-cn": "平屋顶与低坡屋顶维修",
    "zh-tw": "平屋頂與低坡屋頂維修",
    "pa-in": "ਫਲੈਟ-ਛੱਤ-ਮੁਰੰਮਤ",
    "ko-kr": "평지붕-수리",
    "fa-ir": "تعمیر-سقف-تخت",
    "ar-sa": "إصلاح-السقف-المسطح",
  }),
  "metal-roof-repair-maintenance": slugAll("metal-roof-repair-maintenance", {
    "zh-cn": "金属屋顶维修与维护",
    "zh-tw": "金屬屋頂維修與維護",
    "pa-in": "ਧਾਤ-ਛੱਤ-ਮੁਰੰਮਤ",
    "ko-kr": "금속-지붕-수리",
    "fa-ir": "تعمیر-سقف-فلزی",
    "ar-sa": "إصلاح-السقف-المعدني",
  }),
  "cedar-shake-roof-replacement": slugAll("cedar-shake-roof-replacement", {
    "zh-cn": "雪松木瓦屋顶更换",
    "zh-tw": "雪松木瓦屋頂更換",
    "pa-in": "ਸੀਡਰ-ਸ਼ੇਕ-ਬਦਲੀ",
    "ko-kr": "시더-쉐이크-교체",
    "fa-ir": "تعویض-سقف-سدر",
    "ar-sa": "استبدال-سقف-الأرز",
  }),
  "metal-roof-replacement": slugAll("metal-roof-replacement", {
    "zh-cn": "金属屋顶更换",
    "zh-tw": "金屬屋頂更換",
    "pa-in": "ਧਾਤ-ਛੱਤ-ਬਦਲੀ",
    "ko-kr": "금속-지붕-교체",
    "fa-ir": "تعویض-سقف-فلزی",
    "ar-sa": "استبدال-السقف-المعدني",
  }),
  "flat-roof-replacement-sbs-tpo-pvc": slugAll("flat-roof-replacement-sbs-tpo-pvc", {
    "zh-cn": "平屋顶膜材更换",
    "zh-tw": "平屋頂膜材更換",
    "pa-in": "ਫਲੈਟ-ਝਿੱਲੀ-ਬਦਲੀ",
    "ko-kr": "평지붕-막재-교체",
    "fa-ir": "تعویض-غشای-سقف-تخت",
    "ar-sa": "استبدال-غشاء-السقف-المسطح",
  }),
};

export const SERVICE_AREA_ENTITY_SLUGS: Record<string, LocalizedSlugMap> = {
  vancouver: slugAll("vancouver", {
    "zh-cn": "温哥华",
    "zh-tw": "溫哥華",
    "pa-in": "ਵੈਨਕੂਵਰ",
    "ko-kr": "밴쿠버",
    "fa-ir": "ونکوور",
    "ar-sa": "فانكوفر",
  }),
  burnaby: slugAll("burnaby", {
    "zh-cn": "本拿比",
    "zh-tw": "本拿比",
    "pa-in": "ਬਰਨਬੀ",
    "ko-kr": "버나비",
    "fa-ir": "برنابی",
    "ar-sa": "بيرنابي",
  }),
  coquitlam: slugAll("coquitlam", {
    "zh-cn": "高贵林",
    "zh-tw": "高貴林",
    "pa-in": "ਕੋਕੁਇਟਲਮ",
    "ko-kr": "코퀴틀람",
    "fa-ir": "کوکیتلام",
    "ar-sa": "كوكويتلام",
  }),
  surrey: slugAll("surrey", {
    "zh-cn": "素里",
    "zh-tw": "素里",
    "pa-in": "ਸਰੀ",
    "ko-kr": "써리",
    "fa-ir": "سری",
    "ar-sa": "سري",
  }),
  richmond: slugAll("richmond", {
    "zh-cn": "列治文",
    "zh-tw": "列治文",
    "pa-in": "ਰਿਚਮੰਡ",
    "ko-kr": "리치먼드",
    "fa-ir": "ریچموند",
    "ar-sa": "ريتشموند",
  }),
  "north-vancouver": slugAll("north-vancouver", {
    "zh-cn": "北温哥华",
    "zh-tw": "北溫哥華",
    "pa-in": "ਉੱਤਰੀ-ਵੈਨਕੂਵਰ",
    "ko-kr": "노스밴쿠버",
    "fa-ir": "ونکوور-شمالی",
    "ar-sa": "فانكوفر-الشمالية",
  }),
  "west-vancouver": slugAll("west-vancouver", {
    "zh-cn": "西温哥华",
    "zh-tw": "西溫哥華",
    "pa-in": "ਪੱਛਮੀ-ਵੈਨਕੂਵਰ",
    "ko-kr": "웨스트밴쿠버",
    "fa-ir": "ونکوور-غربی",
    "ar-sa": "فانكوفر-الغربية",
  }),
  "new-westminster": slugAll("new-westminster", {
    "zh-cn": "新西敏",
    "zh-tw": "新西敏",
    "pa-in": "ਨਿਊ-ਵੈਸਟਮਿਨਸਟਰ",
    "ko-kr": "뉴웨스트민스터",
    "fa-ir": "نیو-وست‌مینستر",
    "ar-sa": "نيوويستمنستر",
  }),
  "port-coquitlam": slugAll("port-coquitlam", {
    "zh-cn": "高贵林港",
    "zh-tw": "高貴林港",
    "pa-in": "ਪੋਰਟ-ਕੋਕੁਇਟਲਮ",
    "ko-kr": "포트코퀴틀람",
    "fa-ir": "پورت-کوکیتلام",
    "ar-sa": "بورت-كوكويتلام",
  }),
  "port-moody": slugAll("port-moody", {
    "zh-cn": "满地宝港",
    "zh-tw": "滿地寶港",
    "pa-in": "ਪੋਰਟ-ਮੂਡੀ",
    "ko-kr": "포트무디",
    "fa-ir": "پورت-مودی",
    "ar-sa": "بورت-مودي",
  }),
  "maple-ridge": slugAll("maple-ridge", {
    "zh-cn": "枫树岭",
    "zh-tw": "楓樹嶺",
    "pa-in": "ਮੇਪਲ-ਰਿਜ",
    "ko-kr": "메이플리지",
    "fa-ir": "میپل-ریج",
    "ar-sa": "ميبل-ريدج",
  }),
  delta: slugAll("delta", {
    "zh-cn": "三角洲",
    "zh-tw": "三角洲",
    "pa-in": "ਡੈਲਟਾ",
    "ko-kr": "델타",
    "fa-ir": "دلتا",
    "ar-sa": "دلتا",
  }),
  langley: slugAll("langley", {
    "zh-cn": "兰里",
    "zh-tw": "蘭里",
    "pa-in": "ਲੈਂਗਲੀ",
    "ko-kr": "랭리",
    "fa-ir": "لنگلی",
    "ar-sa": "لانغلي",
  }),
  "white-rock": slugAll("white-rock", {
    "zh-cn": "白石",
    "zh-tw": "白石",
    "pa-in": "ਵਾਈਟ-ਰੌਕ",
    "ko-kr": "화이트록",
    "fa-ir": "وایت-راک",
    "ar-sa": "وايت-روك",
  }),
};

export const PROJECT_ENTITY_SLUGS: Record<string, LocalizedSlugMap> = {
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "asphalt-shingle-replacement-burnaby"
  "asphalt-shingle-replacement-burnaby": slugAll("asphalt-shingle-replacement-burnaby", {
    "zh-cn": "沥青瓦屋顶更换-本拿比-01",
    "zh-tw": "瀝青瓦屋頂更換-本拿比-01",
    "pa-in": "ਉਦਾਹਰਨ-ਅਸਫਾਲਟ-ਸ਼ਿੰਗਲ-ਬਦਲੀ-ਬਰਨਬੀ-01",
    "ko-kr": "예시-아스팔트-싱글-교체-버나비-01",
    "fa-ir": "نمونه-تعویض-شینگل-آسفالتی-برنابی-01",
    "ar-sa": "مثال-استبدال-الألواح-الإسفلتية-بيرنابي-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "asphalt-shingle-replacement-surrey"
  "asphalt-shingle-replacement-surrey": slugAll("asphalt-shingle-replacement-surrey", {
    "zh-cn": "沥青瓦屋顶更换-素里-02",
    "zh-tw": "瀝青瓦屋頂更換-素里-02",
    "pa-in": "ਉਦਾਹਰਨ-ਅਸਫਾਲਟ-ਸ਼ਿੰਗਲ-ਬਦਲੀ-ਸਰੀ-02",
    "ko-kr": "예시-아스팔트-싱글-교체-써리-02",
    "fa-ir": "نمونه-تعویض-شینگل-آسفالتی-سری-02",
    "ar-sa": "مثال-استبدال-الألواح-الإسفلتية-سري-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "emergency-roof-leak-repair-vancouver"
  "emergency-roof-leak-repair-vancouver": slugAll("emergency-roof-leak-repair-vancouver", {
    "zh-cn": "紧急屋顶漏水维修-温哥华-01",
    "zh-tw": "緊急屋頂漏水維修-溫哥華-01",
    "pa-in": "ਉਦਾਹਰਨ-ਐਮਰਜੈਂਸੀ-ਛੱਤ-ਲੀਕ-ਮੁਰੰਮਤ-ਵੈਨਕੂਵਰ-01",
    "ko-kr": "예시-긴급-지붕-누수-수리-밴쿠버-01",
    "fa-ir": "نمونه-تعمیر-اضطراری-نشت-سقف-ونکوور-01",
    "ar-sa": "مثال-إصلاح-تسرب-الطوارئ-فانكوفر-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "emergency-roof-leak-repair-north-vancouver"
  "emergency-roof-leak-repair-north-vancouver": slugAll(
    "emergency-roof-leak-repair-north-vancouver",
    {
      "zh-cn": "紧急屋顶漏水维修-北温哥华-02",
      "zh-tw": "緊急屋頂漏水維修-北溫哥華-02",
      "pa-in": "ਉਦਾਹਰਨ-ਐਮਰਜੈਂਸੀ-ਛੱਤ-ਲੀਕ-ਮੁਰੰਮਤ-ਉੱਤਰੀ-ਵੈਨਕੂਵਰ-02",
      "ko-kr": "예시-긴급-지붕-누수-수리-노스밴쿠버-02",
      "fa-ir": "نمونه-تعمیر-اضطراری-نشت-سقف-ونکوور-شمالی-02",
      "ar-sa": "مثال-إصلاح-تسرب-الطوارئ-فانكوفر-الشمالية-02",
    },
  ),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "shingle-spot-repair-coquitlam"
  "shingle-spot-repair-coquitlam": slugAll("shingle-spot-repair-coquitlam", {
    "zh-cn": "瓦片局部维修-高贵林-01",
    "zh-tw": "瓦片局部維修-高貴林-01",
    "pa-in": "ਉਦਾਹਰਨ-ਸ਼ਿੰਗਲ-ਸਥਾਨਕ-ਮੁਰੰਮਤ-ਕੋਕੁਇਟਲਮ-01",
    "ko-kr": "예시-싱글-부분-수리-코퀴틀람-01",
    "fa-ir": "نمونه-تعمیر-موضعی-شینگل-کوکیتلام-01",
    "ar-sa": "مثال-إصلاح-موضعي-للألواح-كوكويتلام-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "shingle-spot-repair-new-westminster"
  "shingle-spot-repair-new-westminster": slugAll("shingle-spot-repair-new-westminster", {
    "zh-cn": "瓦片局部维修-新西敏-02",
    "zh-tw": "瓦片局部維修-新西敏-02",
    "pa-in": "ਉਦਾਹਰਨ-ਸ਼ਿੰਗਲ-ਸਥਾਨਕ-ਮੁਰੰਮਤ-ਨਿਊ-ਵੈਸਟਮਿਨਸਟਰ-02",
    "ko-kr": "예시-싱글-부분-수리-뉴웨스트민스터-02",
    "fa-ir": "نمونه-تعمیر-موضعی-شینگل-نیو-وست‌مینستر-02",
    "ar-sa": "مثال-إصلاح-موضعي-للألواح-نيوويستمنستر-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "flat-low-slope-roof-repair-richmond"
  "flat-low-slope-roof-repair-richmond": slugAll("flat-low-slope-roof-repair-richmond", {
    "zh-cn": "平屋顶与低坡屋顶维修-列治文-01",
    "zh-tw": "平屋頂與低坡屋頂維修-列治文-01",
    "pa-in": "ਉਦਾਹਰਨ-ਫਲੈਟ-ਛੱਤ-ਮੁਰੰਮਤ-ਰਿਚਮੰਡ-01",
    "ko-kr": "예시-평지붕-수리-리치먼드-01",
    "fa-ir": "نمونه-تعمیر-سقف-تخت-ریچموند-01",
    "ar-sa": "مثال-إصلاح-السقف-المسطح-ريتشموند-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "flat-low-slope-roof-repair-burnaby"
  "flat-low-slope-roof-repair-burnaby": slugAll("flat-low-slope-roof-repair-burnaby", {
    "zh-cn": "平屋顶与低坡屋顶维修-本拿比-02",
    "zh-tw": "平屋頂與低坡屋頂維修-本拿比-02",
    "pa-in": "ਉਦਾਹਰਨ-ਫਲੈਟ-ਛੱਤ-ਮੁਰੰਮਤ-ਬਰਨਬੀ-02",
    "ko-kr": "예시-평지붕-수리-버나비-02",
    "fa-ir": "نمونه-تعمیر-سقف-تخت-برنابی-02",
    "ar-sa": "مثال-إصلاح-السقف-المسطح-بيرنابي-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "moss-removal-roof-gutter-coquitlam"
  "moss-removal-roof-gutter-coquitlam": slugAll("moss-removal-roof-gutter-coquitlam", {
    "zh-cn": "苔藓清除与檐沟清理-高贵林-01",
    "zh-tw": "苔蘚清除與簷溝清理-高貴林-01",
    "pa-in": "ਉਦਾਹਰਨ-ਕਾਈ-ਹਟਾਉਣਾ-ਗਟਰ-ਕੋਕੁਇਟਲਮ-01",
    "ko-kr": "예시-이끼-제거-빗물받이-코퀴틀람-01",
    "fa-ir": "نمونه-حذف-خزه-و-ناودان-کوکیتلام-01",
    "ar-sa": "مثال-إزالة-الطحالب-والمزاريب-كوكويتلام-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "moss-removal-roof-gutter-maple-ridge"
  "moss-removal-roof-gutter-maple-ridge": slugAll("moss-removal-roof-gutter-maple-ridge", {
    "zh-cn": "苔藓清除与檐沟清理-枫树岭-02",
    "zh-tw": "苔蘚清除與簷溝清理-楓樹嶺-02",
    "pa-in": "ਉਦਾਹਰਨ-ਕਾਈ-ਹਟਾਉਣਾ-ਗਟਰ-ਮੇਪਲ-ਰਿਜ-02",
    "ko-kr": "예시-이끼-제거-빗물받이-메이플리지-02",
    "fa-ir": "نمونه-حذف-خزه-و-ناودان-میپل-ریج-02",
    "ar-sa": "مثال-إزالة-الطحالب-والمزاريب-ميبل-ريدج-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "gutter-replacement-delta"
  "gutter-replacement-delta": slugAll("gutter-replacement-delta", {
    "zh-cn": "檐沟更换-三角洲-01",
    "zh-tw": "簷溝更換-三角洲-01",
    "pa-in": "ਉਦਾਹਰਨ-ਗਟਰ-ਬਦਲੀ-ਡੈਲਟਾ-01",
    "ko-kr": "예시-빗물받이-교체-델타-01",
    "fa-ir": "نمونه-تعویض-ناودان-دلتا-01",
    "ar-sa": "مثال-استبدال-المزاريب-دلتا-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "gutter-replacement-white-rock"
  "gutter-replacement-white-rock": slugAll("gutter-replacement-white-rock", {
    "zh-cn": "檐沟更换-白石-02",
    "zh-tw": "簷溝更換-白石-02",
    "pa-in": "ਉਦਾਹਰਨ-ਗਟਰ-ਬਦਲੀ-ਵਾਈਟ-ਰੌਕ-02",
    "ko-kr": "예시-빗물받이-교체-화이트록-02",
    "fa-ir": "نمونه-تعویض-ناودان-وایت-راک-02",
    "ar-sa": "مثال-استبدال-المزاريب-وايت-روك-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "cedar-shake-repair-north-vancouver"
  "cedar-shake-repair-north-vancouver": slugAll("cedar-shake-repair-north-vancouver", {
    "zh-cn": "雪松木瓦维修与修复-北温哥华-01",
    "zh-tw": "雪松木瓦維修與修復-北溫哥華-01",
    "pa-in": "ਉਦਾਹਰਨ-ਸੀਡਰ-ਸ਼ੇਕ-ਮੁਰੰਮਤ-ਉੱਤਰੀ-ਵੈਨਕੂਵਰ-01",
    "ko-kr": "예시-시더-쉐이크-수리-노스밴쿠버-01",
    "fa-ir": "نمونه-تعمیر-شینگل-سدر-ونکوور-شمالی-01",
    "ar-sa": "مثال-إصلاح-ألواح-الأرز-فانكوفر-الشمالية-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "cedar-shake-repair-west-vancouver"
  "cedar-shake-repair-west-vancouver": slugAll("cedar-shake-repair-west-vancouver", {
    "zh-cn": "雪松木瓦维修与修复-西温哥华-02",
    "zh-tw": "雪松木瓦維修與修復-西溫哥華-02",
    "pa-in": "ਉਦਾਹਰਨ-ਸੀਡਰ-ਸ਼ੇਕ-ਮੁਰੰਮਤ-ਪੱਛਮੀ-ਵੈਨਕੂਵਰ-02",
    "ko-kr": "예시-시더-쉐이크-수리-웨스트밴쿠버-02",
    "fa-ir": "نمونه-تعمیر-شینگل-سدر-ونکوور-غربی-02",
    "ar-sa": "مثال-إصلاح-ألواح-الأرز-فانكوفر-الغربية-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "metal-roof-repair-langley"
  "metal-roof-repair-langley": slugAll("metal-roof-repair-langley", {
    "zh-cn": "金属屋顶维修与维护-兰里-01",
    "zh-tw": "金屬屋頂維修與維護-蘭里-01",
    "pa-in": "ਉਦਾਹਰਨ-ਧਾਤ-ਛੱਤ-ਮੁਰੰਮਤ-ਲੈਂਗਲੀ-01",
    "ko-kr": "예시-금속-지붕-수리-랭리-01",
    "fa-ir": "نمونه-تعمیر-سقف-فلزی-لنگلی-01",
    "ar-sa": "مثال-إصلاح-السقف-المعدني-لانغلي-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "metal-roof-repair-port-moody"
  "metal-roof-repair-port-moody": slugAll("metal-roof-repair-port-moody", {
    "zh-cn": "金属屋顶维修与维护-满地宝港-02",
    "zh-tw": "金屬屋頂維修與維護-滿地寶港-02",
    "pa-in": "ਉਦਾਹਰਨ-ਧਾਤ-ਛੱਤ-ਮੁਰੰਮਤ-ਪੋਰਟ-ਮੂਡੀ-02",
    "ko-kr": "예시-금속-지붕-수리-포트무디-02",
    "fa-ir": "نمونه-تعمیر-سقف-فلزی-پورت-مودی-02",
    "ar-sa": "مثال-إصلاح-السقف-المعدني-بورت-مودي-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "cedar-shake-replacement-west-vancouver"
  "cedar-shake-replacement-west-vancouver": slugAll("cedar-shake-replacement-west-vancouver", {
    "zh-cn": "雪松木瓦屋顶更换-西温哥华-01",
    "zh-tw": "雪松木瓦屋頂更換-西溫哥華-01",
    "pa-in": "ਉਦਾਹਰਨ-ਸੀਡਰ-ਸ਼ੇਕ-ਬਦਲੀ-ਪੱਛਮੀ-ਵੈਨਕੂਵਰ-01",
    "ko-kr": "예시-시더-쉐이크-교체-웨스트밴쿠버-01",
    "fa-ir": "نمونه-تعویض-سقف-سدر-ونکوور-غربی-01",
    "ar-sa": "مثال-استبدال-سقف-الأرز-فانكوفر-الغربية-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "cedar-shake-replacement-north-vancouver"
  "cedar-shake-replacement-north-vancouver": slugAll("cedar-shake-replacement-north-vancouver", {
    "zh-cn": "雪松木瓦屋顶更换-北温哥华-02",
    "zh-tw": "雪松木瓦屋頂更換-北溫哥華-02",
    "pa-in": "ਉਦਾਹਰਨ-ਸੀਡਰ-ਸ਼ੇਕ-ਬਦਲੀ-ਉੱਤਰੀ-ਵੈਨਕੂਵਰ-02",
    "ko-kr": "예시-시더-쉐이크-교체-노스밴쿠버-02",
    "fa-ir": "نمونه-تعویض-سقف-سدر-ونکوور-شمالی-02",
    "ar-sa": "مثال-استبدال-سقف-الأرز-فانكوفر-الشمالية-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "metal-roof-replacement-west-vancouver"
  "metal-roof-replacement-west-vancouver": slugAll("metal-roof-replacement-west-vancouver", {
    "zh-cn": "金属屋顶更换-西温哥华-01",
    "zh-tw": "金屬屋頂更換-西溫哥華-01",
    "pa-in": "ਉਦਾਹਰਨ-ਧਾਤ-ਛੱਤ-ਬਦਲੀ-ਪੱਛਮੀ-ਵੈਨਕੂਵਰ-01",
    "ko-kr": "예시-금속-지붕-교체-웨스트밴쿠버-01",
    "fa-ir": "نمونه-تعویض-سقف-فلزی-ونکوور-غربی-01",
    "ar-sa": "مثال-استبدال-السقف-المعدني-فانكوفر-الغربية-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "metal-roof-replacement-coquitlam"
  "metal-roof-replacement-coquitlam": slugAll("metal-roof-replacement-coquitlam", {
    "zh-cn": "金属屋顶更换-高贵林-02",
    "zh-tw": "金屬屋頂更換-高貴林-02",
    "pa-in": "ਉਦਾਹਰਨ-ਧਾਤ-ਛੱਤ-ਬਦਲੀ-ਕੋਕੁਇਟਲਮ-02",
    "ko-kr": "예시-금속-지붕-교체-코퀴틀람-02",
    "fa-ir": "نمونه-تعویض-سقف-فلزی-کوکیتلام-02",
    "ar-sa": "مثال-استبدال-السقف-المعدني-كوكويتلام-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "flat-roof-replacement-vancouver"
  "flat-roof-replacement-vancouver": slugAll("flat-roof-replacement-vancouver", {
    "zh-cn": "平屋顶膜材更换-温哥华-01",
    "zh-tw": "平屋頂膜材更換-溫哥華-01",
    "pa-in": "ਉਦਾਹਰਨ-ਫਲੈਟ-ਝਿੱਲੀ-ਬਦਲੀ-ਵੈਨਕੂਵਰ-01",
    "ko-kr": "예시-평지붕-막재-교체-밴쿠버-01",
    "fa-ir": "نمونه-تعویض-غشای-سقف-تخت-ونکوور-01",
    "ar-sa": "مثال-استبدال-غشاء-السقف-المسطح-فانكوفر-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "flat-roof-replacement-richmond"
  "flat-roof-replacement-richmond": slugAll("flat-roof-replacement-richmond", {
    "zh-cn": "平屋顶膜材更换-列治文-02",
    "zh-tw": "平屋頂膜材更換-列治文-02",
    "pa-in": "ਉਦਾਹਰਨ-ਫਲੈਟ-ਝਿੱਲੀ-ਬਦਲੀ-ਰਿਚਮੰਡ-02",
    "ko-kr": "예시-평지붕-막재-교체-리치먼드-02",
    "fa-ir": "نمونه-تعویض-غشای-سقف-تخت-ریچموند-02",
    "ar-sa": "مثال-استبدال-غشاء-السقف-المسطح-ريتشموند-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "flashing-repair-burnaby"
  "flashing-repair-burnaby": slugAll("flashing-repair-burnaby", {
    "zh-cn": "泛水与穿透点维修-本拿比-01",
    "zh-tw": "泛水與穿透點維修-本拿比-01",
    "pa-in": "ਉਦਾਹਰਨ-ਫਲੈਸ਼ਿੰਗ-ਪ੍ਰਵੇਸ਼-ਮੁਰੰਮਤ-ਬਰਨਬੀ-01",
    "ko-kr": "예시-플래싱-관통부-수리-버나비-01",
    "fa-ir": "نمونه-تعمیر-فلاشینگ-و-نفوذ-برنابی-01",
    "ar-sa": "مثال-إصلاح-الفلاشينغ-والاختراق-بيرنابي-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "flashing-repair-vancouver"
  "flashing-repair-vancouver": slugAll("flashing-repair-vancouver", {
    "zh-cn": "泛水与穿透点维修-温哥华-02",
    "zh-tw": "泛水與穿透點維修-溫哥華-02",
    "pa-in": "ਉਦਾਹਰਨ-ਫਲੈਸ਼ਿੰਗ-ਪ੍ਰਵੇਸ਼-ਮੁਰੰਮਤ-ਵੈਨਕੂਵਰ-02",
    "ko-kr": "예시-플래싱-관통부-수리-밴쿠버-02",
    "fa-ir": "نمونه-تعمیر-فلاشینگ-و-نفوذ-ونکوور-02",
    "ar-sa": "مثال-إصلاح-الفلاشينغ-والاختراق-فانكوفر-02",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "skylight-flashing-repair-north-vancouver"
  "skylight-flashing-repair-north-vancouver": slugAll("skylight-flashing-repair-north-vancouver", {
    "zh-cn": "天窗漏水与泛水维修-北温哥华-01",
    "zh-tw": "天窗漏水與泛水維修-北溫哥華-01",
    "pa-in": "ਉਦਾਹਰਨ-ਸਕਾਈਲਾਈਟ-ਲੀਕ-ਮੁਰੰਮਤ-ਉੱਤਰੀ-ਵੈਨਕੂਵਰ-01",
    "ko-kr": "예시-천창-누수-수리-노스밴쿠버-01",
    "fa-ir": "نمونه-تعمیر-نشت-نورگیر-ونکوور-شمالی-01",
    "ar-sa": "مثال-إصلاح-تسرب-النوافذ-السقفية-فانكوفر-الشمالية-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "chimney-flashing-repair-new-westminster"
  "chimney-flashing-repair-new-westminster": slugAll("chimney-flashing-repair-new-westminster", {
    "zh-cn": "烟囱泛水维修-新西敏-01",
    "zh-tw": "煙囪泛水維修-新西敏-01",
    "pa-in": "ਉਦਾਹਰਨ-ਚਿਮਨੀ-ਫਲੈਸ਼ਿੰਗ-ਮੁਰੰਮਤ-ਨਿਊ-ਵੈਸਟਮਿਨਸਟਰ-01",
    "ko-kr": "예시-굴뚝-플래싱-수리-뉴웨스트민스터-01",
    "fa-ir": "نمونه-تعمیر-فلاشینگ-دودکش-نیو-وست‌مینستر-01",
    "ar-sa": "مثال-إصلاح-فلاشينغ-المدخنة-نيوويستمنستر-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "gutter-cleaning-port-coquitlam"
  "gutter-cleaning-port-coquitlam": slugAll("gutter-cleaning-port-coquitlam", {
    "zh-cn": "檐沟清洁与落水管冲洗-高贵林港-01",
    "zh-tw": "簷溝清潔與落水管沖洗-高貴林港-01",
    "pa-in": "ਉਦਾਹਰਨ-ਗਟਰ-ਸਫਾਈ-ਪੋਰਟ-ਕੋਕੁਇਟਲਮ-01",
    "ko-kr": "예시-빗물받이-청소-포트코퀴틀람-01",
    "fa-ir": "نمونه-نظافت-ناودان-پورت-کوکیتلام-01",
    "ar-sa": "مثال-تنظيف-المزاريب-بورت-كوكويتلام-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "gutter-guard-port-moody"
  "gutter-guard-port-moody": slugAll("gutter-guard-port-moody", {
    "zh-cn": "檐沟防护网安装-满地宝港-01",
    "zh-tw": "簷溝防護網安裝-滿地寶港-01",
    "pa-in": "ਉਦਾਹਰਨ-ਗਟਰ-ਗਾਰਡ-ਇੰਸਟਾਲ-ਪੋਰਟ-ਮੂਡੀ-01",
    "ko-kr": "예시-빗물받이-가드-설치-포트무디-01",
    "fa-ir": "نمونه-نصب-محافظ-ناودان-پورت-مودی-01",
    "ar-sa": "مثال-تركيب-حماية-المزاريب-بورت-مودي-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "roof-ventilation-upgrade-burnaby"
  "roof-ventilation-upgrade-burnaby": slugAll("roof-ventilation-upgrade-burnaby", {
    "zh-cn": "屋顶通风升级-本拿比-01",
    "zh-tw": "屋頂通風升級-本拿比-01",
    "pa-in": "ਉਦਾਹਰਨ-ਛੱਤ-ਹਵਾਦਾਰੀ-ਅਪਗ੍ਰੇਡ-ਬਰਨਬੀ-01",
    "ko-kr": "예시-지붕-환기-개선-버나비-01",
    "fa-ir": "نمونه-ارتقای-تهویه-سقف-برنابی-01",
    "ar-sa": "مثال-ترقية-تهوية-السقف-بيرنابي-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "fascia-soffit-repair-delta"
  "fascia-soffit-repair-delta": slugAll("fascia-soffit-repair-delta", {
    "zh-cn": "封檐板与檐底维修更换-三角洲-01",
    "zh-tw": "封簷板與簷底維修更換-三角洲-01",
    "pa-in": "ਉਦਾਹਰਨ-ਫੈਸੀਆ-ਸੌਫਿਟ-ਮੁਰੰਮਤ-ਡੈਲਟਾ-01",
    "ko-kr": "예시-페시아-소피트-수리-델타-01",
    "fa-ir": "نمونه-تعمیر-فاسیا-و-سافیت-دلتا-01",
    "ar-sa": "مثال-إصلاح-الواجهات-والسقف-المخفي-دلتا-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "storm-tarping-surrey"
  "storm-tarping-surrey": slugAll("storm-tarping-surrey", {
    "zh-cn": "风暴临时遮盖与防雨-素里-01",
    "zh-tw": "風暴臨時遮蓋與防雨-素里-01",
    "pa-in": "ਉਦਾਹਰਨ-ਤੂਫਾਨ-ਤਰਪਾਲ-ਸਰੀ-01",
    "ko-kr": "예시-폭풍-방수-덮개-써리-01",
    "fa-ir": "نمونه-پوشش-اضطراری-طوفان-سری-01",
    "ar-sa": "مثال-تغطية-طوارئ-العواصف-سري-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "roof-health-check-langley"
  "roof-health-check-langley": slugAll("roof-health-check-langley", {
    "zh-cn": "屋顶健康检查与维护-兰里-01",
    "zh-tw": "屋頂健康檢查與維護-蘭里-01",
    "pa-in": "ਉਦਾਹਰਨ-ਛੱਤ-ਜਾਂਚ-ਰੱਖ-ਰਖਾਵ-ਲੈਂਗਲੀ-01",
    "ko-kr": "예시-지붕-점검-유지관리-랭리-01",
    "fa-ir": "نمونه-بازرسی-و-نگهداری-سقف-لنگلی-01",
    "ar-sa": "مثال-فحص-وصيانة-السقف-لانغلي-01",
  }),
  // NEEDS HUMAN REVIEW: pa-in / ko-kr / fa-ir / ar-sa composition for "moss-removal-roof-gutter-west-vancouver"
  "moss-removal-roof-gutter-west-vancouver": slugAll("moss-removal-roof-gutter-west-vancouver", {
    "zh-cn": "苔藓清除与檐沟清理-西温哥华-03",
    "zh-tw": "苔蘚清除與簷溝清理-西溫哥華-03",
    "pa-in": "ਉਦਾਹਰਨ-ਕਾਈ-ਹਟਾਉਣਾ-ਗਟਰ-ਪੱਛਮੀ-ਵੈਨਕੂਵਰ-03",
    "ko-kr": "예시-이끼-제거-빗물받이-웨스트밴쿠버-03",
    "fa-ir": "نمونه-حذف-خزه-و-ناودان-ونکوور-غربی-03",
    "ar-sa": "مثال-إزالة-الطحالب-والمزاريب-فانكوفر-الغربية-03",
  }),
};

/**
 * Old English project URL segments that contained "illustrative-" → current entity IDs.
 * Used for single-hop 301s; not registered as live routes.
 */
export const LEGACY_PROJECT_ENTITY_SLUGS: Record<string, string> = {
  "illustrative-asphalt-shingle-replacement-burnaby": "asphalt-shingle-replacement-burnaby",
  "illustrative-asphalt-shingle-replacement-surrey": "asphalt-shingle-replacement-surrey",
  "illustrative-emergency-roof-leak-repair-vancouver": "emergency-roof-leak-repair-vancouver",
  "illustrative-emergency-roof-leak-repair-north-vancouver":
    "emergency-roof-leak-repair-north-vancouver",
  "illustrative-shingle-spot-repair-coquitlam": "shingle-spot-repair-coquitlam",
  "illustrative-shingle-spot-repair-new-westminster": "shingle-spot-repair-new-westminster",
  "illustrative-flat-low-slope-roof-repair-richmond": "flat-low-slope-roof-repair-richmond",
  "illustrative-flat-low-slope-roof-repair-burnaby": "flat-low-slope-roof-repair-burnaby",
  "illustrative-moss-removal-roof-gutter-coquitlam": "moss-removal-roof-gutter-coquitlam",
  "illustrative-moss-removal-roof-gutter-maple-ridge": "moss-removal-roof-gutter-maple-ridge",
  "illustrative-gutter-replacement-delta": "gutter-replacement-delta",
  "illustrative-gutter-replacement-white-rock": "gutter-replacement-white-rock",
  "illustrative-cedar-shake-repair-north-vancouver": "cedar-shake-repair-north-vancouver",
  "illustrative-cedar-shake-repair-west-vancouver": "cedar-shake-repair-west-vancouver",
  "illustrative-metal-roof-repair-langley": "metal-roof-repair-langley",
  "illustrative-metal-roof-repair-port-moody": "metal-roof-repair-port-moody",
  "illustrative-cedar-shake-replacement-west-vancouver": "cedar-shake-replacement-west-vancouver",
  "illustrative-cedar-shake-replacement-north-vancouver": "cedar-shake-replacement-north-vancouver",
  "illustrative-metal-roof-replacement-west-vancouver": "metal-roof-replacement-west-vancouver",
  "illustrative-metal-roof-replacement-coquitlam": "metal-roof-replacement-coquitlam",
  "illustrative-flat-roof-replacement-vancouver": "flat-roof-replacement-vancouver",
  "illustrative-flat-roof-replacement-richmond": "flat-roof-replacement-richmond",
  "illustrative-flashing-repair-burnaby": "flashing-repair-burnaby",
  "illustrative-flashing-repair-vancouver": "flashing-repair-vancouver",
  "illustrative-skylight-flashing-repair-north-vancouver":
    "skylight-flashing-repair-north-vancouver",
  "illustrative-chimney-flashing-repair-new-westminster": "chimney-flashing-repair-new-westminster",
  "illustrative-gutter-cleaning-port-coquitlam": "gutter-cleaning-port-coquitlam",
  "illustrative-gutter-guard-port-moody": "gutter-guard-port-moody",
  "illustrative-roof-ventilation-upgrade-burnaby": "roof-ventilation-upgrade-burnaby",
  "illustrative-fascia-soffit-repair-delta": "fascia-soffit-repair-delta",
  "illustrative-storm-tarping-surrey": "storm-tarping-surrey",
  "illustrative-roof-health-check-langley": "roof-health-check-langley",
  "illustrative-moss-removal-roof-gutter-west-vancouver": "moss-removal-roof-gutter-west-vancouver",
};

export const BLOG_ENTITY_SLUGS: Record<string, LocalizedSlugMap> = {
  "signs-roof-needs-repair-vancouver": slugAll("signs-roof-needs-repair-vancouver", {
    "zh-cn": "signs-roof-needs-repair-vancouver",
    "zh-tw": "signs-roof-needs-repair-vancouver",
    "pa-in": "signs-roof-needs-repair-vancouver",
    "ko-kr": "signs-roof-needs-repair-vancouver",
    "fa-ir": "signs-roof-needs-repair-vancouver",
    "ar-sa": "signs-roof-needs-repair-vancouver",
  }),
  "asphalt-shingles-vs-metal-roofing-vancouver": slugAll(
    "asphalt-shingles-vs-metal-roofing-vancouver",
    {
      "zh-cn": "asphalt-shingles-vs-metal-roofing-vancouver",
      "zh-tw": "asphalt-shingles-vs-metal-roofing-vancouver",
      "pa-in": "asphalt-shingles-vs-metal-roofing-vancouver",
      "ko-kr": "asphalt-shingles-vs-metal-roofing-vancouver",
      "fa-ir": "asphalt-shingles-vs-metal-roofing-vancouver",
      "ar-sa": "asphalt-shingles-vs-metal-roofing-vancouver",
    },
  ),
  "ceiling-water-stain-roof-or-plumbing": slugAll("ceiling-water-stain-roof-or-plumbing", {
    "zh-cn": "ceiling-water-stain-roof-or-plumbing",
    "zh-tw": "ceiling-water-stain-roof-or-plumbing",
    "pa-in": "ceiling-water-stain-roof-or-plumbing",
    "ko-kr": "ceiling-water-stain-roof-or-plumbing",
    "fa-ir": "ceiling-water-stain-roof-or-plumbing",
    "ar-sa": "ceiling-water-stain-roof-or-plumbing",
  }),
};

export const CAMPAIGN_ENTITY_SLUGS: Record<string, LocalizedSlugMap> = {
  "roof-replacement-vancouver": slugAll("roof-replacement-vancouver", {
    "zh-cn": "温哥华屋顶更换",
    "zh-tw": "溫哥華屋頂更換",
    "pa-in": "ਵੈਨਕੂਵਰ-ਛੱਤ-ਬਦਲੀ",
    "ko-kr": "밴쿠버-지붕-교체",
    "fa-ir": "تعویض-سقف-ونکوور",
    "ar-sa": "استبدال-سقف-فانكوفر",
  }),
  "roof-repair-vancouver": slugAll("roof-repair-vancouver", {
    "zh-cn": "温哥华屋顶维修",
    "zh-tw": "溫哥華屋頂維修",
    "pa-in": "ਵੈਨਕੂਵਰ-ਛੱਤ-ਮੁਰੰਮਤ",
    "ko-kr": "밴쿠버-지붕-수리",
    "fa-ir": "تعمیر-سقف-ونکوور",
    "ar-sa": "إصلاح-سقف-فانكوفر",
  }),
  "roof-replacement-burnaby": slugAll("roof-replacement-burnaby", {
    "zh-cn": "本拿比屋顶更换",
    "zh-tw": "本拿比屋頂更換",
    "pa-in": "ਬਰਨਬੀ-ਛੱਤ-ਬਦਲੀ",
    "ko-kr": "버나비-지붕-교체",
    "fa-ir": "تعویض-سقف-برنابی",
    "ar-sa": "استبدال-سقف-بيرنابي",
  }),
  "roof-repair-burnaby": slugAll("roof-repair-burnaby", {
    "zh-cn": "本拿比屋顶维修",
    "zh-tw": "本拿比屋頂維修",
    "pa-in": "ਬਰਨਬੀ-ਛੱਤ-ਮੁਰੰਮਤ",
    "ko-kr": "버나비-지붕-수리",
    "fa-ir": "تعمیر-سقف-برنابی",
    "ar-sa": "إصلاح-سقف-بيرنابي",
  }),
  "metal-roofing-vancouver": slugAll("metal-roofing-vancouver", {
    "zh-cn": "温哥华金属屋顶",
    "zh-tw": "溫哥華金屬屋頂",
    "pa-in": "ਵੈਨਕੂਵਰ-ਧਾਤ-ਛੱਤ",
    "ko-kr": "밴쿠버-금속-지붕",
    "fa-ir": "سقف-فلزی-ونکوور",
    "ar-sa": "سقف-معدني-فانكوفر",
  }),
  "flat-roofing-vancouver": slugAll("flat-roofing-vancouver", {
    "zh-cn": "温哥华平屋顶",
    "zh-tw": "溫哥華平屋頂",
    "pa-in": "ਵੈਨਕੂਵਰ-ਫਲੈਟ-ਛੱਤ",
    "ko-kr": "밴쿠버-평지붕",
    "fa-ir": "سقف-تخت-ونکوور",
    "ar-sa": "سقف-مسطح-فانكوفر",
  }),
};

export const LEGACY_ENGLISH_CANONICAL_PATHS: Record<string, string> = {
  "/": "home",
  "/services": "core:services",
  "/service-areas": "core:service-areas",
  "/emergency-roofing": "core:emergency-roofing",
  "/commercial-roofing": "core:commercial-roofing",
  "/strata-roofing": "core:strata-roofing",
  "/residential-roofing": "core:residential-roofing",
  "/roof-systems": "core:roof-systems",
  "/roof-estimate": "core:roof-estimate",
  "/service-areas/british-columbia": "core:british-columbia",
  "/before-after": "core:before-after",
  "/blog": "core:blog",
  "/about": "core:about",
  "/faq": "core:faq",
  "/contact": "core:contact",
  "/privacy": "core:privacy",
  "/terms": "core:terms",
  "/thank-you": "core:thank-you",
};
